import { Icon } from '@dev-spendesk/grapes';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { type RateOption } from '../../types';

type Props = {
  rateOptions: RateOption[];
  selectedRateId: string | null;
  onSelectRate: (rateId: string) => void;
};

const getBoardTypeLabel = (boardType: string): string => {
  const labels: Record<string, string> = {
    room_only: 'Room only',
    breakfast: 'Breakfast included',
    half_board: 'Half board',
    full_board: 'Full board',
  };
  return labels[boardType] || boardType;
};

export const RateSelection = ({
  rateOptions,
  selectedRateId,
  onSelectRate,
}: Props) => {
  const { t } = useTranslation('global');

  if (rateOptions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-16">
      <h3 className="title-l">
        {t('travel.propertyDetails.rateSelection.chooseRate')}
      </h3>
      <div className="grid grid-cols-2 gap-8">
        {rateOptions.map((rate) => (
          <RateOptionCard
            key={rate.id}
            rate={rate}
            isSelected={selectedRateId === rate.id}
            onSelect={() => onSelectRate(rate.id)}
          />
        ))}
      </div>
    </div>
  );
};

const RateOptionCard = ({
  rate,
  isSelected,
  onSelect,
}: {
  rate: RateOption;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { t } = useTranslation('global');

  const isRefundable = rate.refundInformation.type === 'refundable';
  const hasBreakfast = rate.mealInformation.boardType !== 'room_only';

  const features = [
    {
      text: isRefundable
        ? t('travel.propertyDetails.rateSelection.features.freeCancellation')
        : t('travel.propertyDetails.rateSelection.features.noRefund'),
      included: isRefundable,
    },
    {
      text: getBoardTypeLabel(rate.mealInformation.boardType),
      included: hasBreakfast,
    },
  ];

  const getTitle = () => {
    if (isRefundable && hasBreakfast) {
      return t(
        'travel.propertyDetails.rateSelection.rateTypes.refundableWithBreakfast',
      );
    }
    if (isRefundable) {
      return t('travel.propertyDetails.rateSelection.rateTypes.refundable');
    }
    if (hasBreakfast) {
      return t(
        'travel.propertyDetails.rateSelection.rateTypes.nonRefundableWithBreakfast',
      );
    }
    return t('travel.propertyDetails.rateSelection.rateTypes.nonRefundable');
  };

  return (
    <label
      className={`rounded-12 flex cursor-pointer flex-col gap-16 border p-16 ${
        isSelected
          ? 'border-border-selected bg-background-primary-selected'
          : 'border-border-default bg-background-primary-default'
      }`}
    >
      <div className="flex items-center gap-16">
        <input
          type="radio"
          name="rate"
          value={rate.id}
          checked={isSelected}
          onChange={onSelect}
          className={`h-16 w-16 shrink-0 rounded-full ${
            isSelected
              ? 'border-border-selected border-5'
              : 'border-border-default border'
          }`}
        />
        <p className="title-m">{getTitle()}</p>
      </div>
      <div className="flex flex-col gap-8">
        {features.map((feature) => (
          <div key={feature.text} className="flex items-center gap-8">
            <Icon
              name={feature.included ? 'circle-check' : 'circle-strikeout'}
              size="m"
              className={
                feature.included
                  ? 'text-content-success-default'
                  : 'text-content-secondary-bg-primary'
              }
            />
            <p className="body-m text-content-secondary-bg-primary">
              {feature.text}
            </p>
          </div>
        ))}
      </div>
      <div className="border-border-default h-px w-full border-t" />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p className="body-s text-content-secondary-bg-primary">
            {t('travel.propertyDetails.rateSelection.features.basePrice')}
          </p>
          <p className="body-s text-content-secondary-bg-primary">
            {formatMonetaryValue(rate.basePrice)}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="body-s text-content-secondary-bg-primary">
            {t('travel.propertyDetails.rateSelection.features.taxesAndFees')}
          </p>
          <p className="body-s text-content-secondary-bg-primary">
            {formatMonetaryValue(rate.tax)}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="body-m text-content-primary">
            {t('travel.propertyDetails.rateSelection.total')}
          </p>
          <p className="title-m">{formatMonetaryValue(rate.totalPrice)}</p>
        </div>
      </div>
    </label>
  );
};
